import type {Comment, Thread} from '@github-ui/diff-lines/conversation-types'
import {isThreadOutdated} from '../../../helpers/thread-navigation'
import {getFirstComment} from '../../../page-data/payloads/thread-previews'
import type {ConversationItem} from './build-conversation-items'
import type {ParsedCommentsFilterKeywords} from './parse-filter-keywords'
import type {CommentsFilterState} from '../use-comments-filter-state'

export type ParsedCommentsFilterState = Omit<CommentsFilterState, 'filterText'> & {
  parsedKeywords?: ParsedCommentsFilterKeywords
}

/**
 * Data on each item that is used to determine if it matches the current filter
 */
interface ItemFilterData {
  authorLogin: string
  body: string
  createdAt: string
  id: string
  isAgent: boolean
  isOutdated: boolean
  isResolved: boolean
  isHidden: boolean
  path: string
  comments: Comment[]
}

function getThreadFilterData(thread: Thread): ItemFilterData {
  const firstComment = getFirstComment(thread)
  const filePath = thread.path ?? ''

  return {
    authorLogin: firstComment?.author?.login ?? '',
    body: firstComment?.body ?? '',
    createdAt: firstComment?.createdAt ?? '',
    id: thread.id,
    isAgent: firstComment?.author?.isAgent ?? false,
    isResolved: thread.isResolved ?? false,
    isOutdated: isThreadOutdated(thread),
    isHidden: firstComment?.isHidden ?? false,
    path: filePath,
    comments: thread.commentsData?.comments ?? [],
  }
}

function getConversationItemFilterData(item: ConversationItem): ItemFilterData {
  switch (item.type) {
    case 'thread':
      return getThreadFilterData(item.data)
    case 'issue-comment':
      return {
        authorLogin: item.data.author?.login ?? '',
        body: item.data.body,
        createdAt: item.data.createdAt,
        id: String(item.data.id),
        isAgent: item.data.author?.isAgent ?? false,
        isResolved: item.data.isHidden && item.data.minimizedReason === 'RESOLVED',
        isOutdated: item.data.isHidden && item.data.minimizedReason === 'OUTDATED',
        isHidden: item.data.isHidden,
        path: '', // issue comments don't have a file path
        comments: [], // issue comments don't have thread comments
      }
    case 'review-summary': {
      // FIXME: filter threads inside of reviews even when we don't filter the reviews themselves
      return {
        authorLogin: item.data.summary.author?.login ?? '',
        body: item.data.summary.body,
        createdAt: item.data.summary.createdAt,
        id: String(item.data.summary.id),
        isAgent: item.data.summary.author?.isAgent ?? false,
        isResolved: item.data.summary.isHidden && item.data.summary.minimizedReason === 'RESOLVED',
        isOutdated: item.data.summary.isHidden && item.data.summary.minimizedReason === 'OUTDATED',
        isHidden: item.data.summary.isHidden,
        path: '',
        comments: [],
      }
    }
  }
}

/**
 * Return a set of thread ids that match the current filter state
 */
export function filterThreads(threadPreviews: Thread[], filterState: ParsedCommentsFilterState) {
  return threadPreviews.filter(thread => filterItem(getThreadFilterData(thread), filterState))
}

export function filterConversationItems(items: ConversationItem[], filterState: ParsedCommentsFilterState) {
  return items.filter(item => filterItem(getConversationItemFilterData(item), filterState))
}

function filterItem(data: ItemFilterData, filterState: ParsedCommentsFilterState) {
  const {parsedKeywords, hideResolvedThreads, hideOutdatedThreads, hideHiddenComments} = filterState

  if (parsedKeywords) {
    const {afterDate, isResolved, remainingText} = parsedKeywords

    // When is:resolved or is:unresolved is in the filter text, use that to filter
    if (isResolved !== null) {
      if (data.isResolved !== isResolved) {
        return false
      }
    } else if (hideResolvedThreads && data.isResolved) {
      return false
    }

    if (hideOutdatedThreads && data.isOutdated) {
      return false
    }

    if (hideHiddenComments && data.isHidden) {
      return false
    }

    if (afterDate && !isAfterDate(data.createdAt, afterDate)) {
      return false
    }

    if (remainingText) {
      const filterTextLower = remainingText.toLowerCase()
      if (
        !data.authorLogin.toLowerCase().includes(filterTextLower) &&
        !data.body.toLowerCase().includes(filterTextLower) &&
        !data.path.toLowerCase().includes(filterTextLower)
      ) {
        return false
      }
    }
  } else {
    if (hideResolvedThreads && data.isResolved) {
      return false
    }
    if (hideOutdatedThreads && data.isOutdated) {
      return false
    }
    if (hideHiddenComments && data.isHidden) {
      return false
    }
  }

  if (filterState.showAgents === false && data.isAgent) {
    return false
  }

  if (filterState.selectedAuthors && !filterState.selectedAuthors.includes(data.authorLogin)) {
    return false
  }

  return true
}

function isAfterDate(createdAt: string, afterDate: Date): boolean {
  const commentDate = new Date(createdAt)
  return !isNaN(commentDate.getTime()) && commentDate > afterDate
}
